// src/pages/Profile.jsx
import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguagesContext";
import { logout, resetPassword } from "../services/authService";
import Navbar from "../components/common/Navbar";

const S = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Syne:wght@700;800&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.pf-pg {
  min-height: 100vh;
  background: #0d0f18;
  color: #fff;
  font-family: 'Inter', sans-serif;
  padding-bottom: 90px;
}

/* ── Hero banner ── */
.pf-hero {
  position: relative;
  overflow: hidden;
  padding: 100px 24px 0;
  background: linear-gradient(180deg, rgba(0,120,242,0.08) 0%, transparent 100%);
}
.pf-hero::before {
  content: '';
  position: absolute;
  top: -60px; left: -60px;
  width: 340px; height: 340px;
  background: radial-gradient(circle, rgba(0,120,242,0.12) 0%, transparent 70%);
  pointer-events: none;
}
.pf-hero::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent);
}

/* Avatar row */
.pf-avatar-row {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  max-width: 720px;
  margin: 0 auto;
  padding-bottom: 28px;
  position: relative; z-index: 1;
}

.pf-avatar {
  width: 88px; height: 88px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0078F2 0%, #00b4ff 100%);
  display: flex; align-items: center; justify-content: center;
  font-family: 'Syne', sans-serif;
  font-size: 32px; font-weight: 800;
  color: #fff;
  flex-shrink: 0;
  border: 3px solid rgba(0,120,242,0.4);
  box-shadow: 0 0 0 1px rgba(255,255,255,0.06), 0 8px 32px rgba(0,120,242,0.25);
  position: relative;
}
.pf-avatar-online {
  position: absolute;
  bottom: 4px; right: 4px;
  width: 14px; height: 14px;
  background: #22c55e;
  border-radius: 50%;
  border: 2.5px solid #0d0f18;
}

.pf-avatar-info { flex: 1; min-width: 0; }
.pf-name {
  font-family: 'Syne', sans-serif;
  font-size: 26px; font-weight: 800;
  color: #fff; letter-spacing: -0.5px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pf-email { font-size: 13px; color: #5a607a; margin-top: 3px; }
.pf-role-badge {
  display: inline-flex; align-items: center; gap: 5px;
  margin-top: 8px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 4px 10px; border-radius: 20px;
}
.pf-role-badge.admin { background: rgba(234,179,8,0.12); border: 1px solid rgba(234,179,8,0.3); color: #eab308; }
.pf-role-badge.customer { background: rgba(0,120,242,0.1); border: 1px solid rgba(0,120,242,0.25); color: #0078F2; }

/* ── Body ── */
.pf-body { max-width: 720px; margin: 0 auto; padding: 28px 24px 0; }

/* Section header */
.pf-section-label {
  font-size: 10px; font-weight: 700; letter-spacing: 1.2px;
  text-transform: uppercase; color: #3a3f58;
  margin-bottom: 10px; padding-left: 2px;
}

/* Card */
.pf-card {
  background: #131620;
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: 20px;
}

/* Info rows */
.pf-info-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  gap: 12px;
}
.pf-info-row:last-child { border-bottom: none; }
.pf-info-label { font-size: 12px; color: #5a607a; font-weight: 500; min-width: 90px; }
.pf-info-value { font-size: 14px; color: #c8d0e8; font-weight: 500; text-align: right; word-break: break-all; }
.pf-info-value.muted { color: #3a3f58; font-style: italic; }

/* Stats row */
.pf-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0;
  margin-bottom: 20px;
}
.pf-stat {
  background: #131620;
  border: 1px solid rgba(255,255,255,0.05);
  padding: 18px 12px;
  text-align: center;
}
.pf-stat:first-child { border-radius: 14px 0 0 14px; }
.pf-stat:last-child  { border-radius: 0 14px 14px 0; }
.pf-stat + .pf-stat  { border-left: none; }
.pf-stat-num {
  font-family: 'Syne', sans-serif;
  font-size: 26px; font-weight: 800; color: #0078F2; line-height: 1;
}
.pf-stat-lbl { font-size: 10px; color: #5a607a; margin-top: 4px; letter-spacing: 0.5px; }

/* Nav links */
.pf-nav-link {
  display: flex; align-items: center; gap: 14px;
  padding: 15px 18px;
  text-decoration: none; color: #c8d0e8;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  font-size: 14px; font-weight: 500;
  transition: background 0.15s;
}
.pf-nav-link:last-child { border-bottom: none; }
.pf-nav-link:hover { background: rgba(255,255,255,0.02); }
.pf-nav-link:active { background: rgba(255,255,255,0.04); }
.pf-nav-link-ico {
  width: 34px; height: 34px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; flex-shrink: 0;
}
.pf-nav-link-body { flex: 1; }
.pf-nav-link-sub { font-size: 11px; color: #5a607a; margin-top: 1px; }
.pf-nav-arrow { color: #3a3f58; font-size: 18px; }

/* Action buttons */
.pf-btn {
  width: 100%; padding: 14px;
  border-radius: 10px; border: none; cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 14px; font-weight: 700;
  transition: all 0.15s; letter-spacing: 0.2px;
}
.pf-btn-reset {
  background: rgba(0,120,242,0.1);
  border: 1px solid rgba(0,120,242,0.2);
  color: #0078F2;
  margin-bottom: 10px;
}
.pf-btn-reset:hover { background: rgba(0,120,242,0.18); }
.pf-btn-reset:disabled { opacity: 0.5; cursor: not-allowed; }

.pf-btn-logout {
  background: rgba(239,68,68,0.08);
  border: 1px solid rgba(239,68,68,0.18);
  color: #ef4444;
}
.pf-btn-logout:hover { background: rgba(239,68,68,0.15); }
.pf-btn-logout:disabled { opacity: 0.5; cursor: not-allowed; }

/* Toast */
.pf-toast {
  position: fixed; bottom: 80px; left: 50%; transform: translateX(-50%);
  background: #1a1f2e; border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px; padding: 12px 20px;
  font-size: 13px; font-weight: 500; color: #c8d0e8;
  z-index: 9999; white-space: nowrap;
  animation: toastIn 0.25s ease;
}
.pf-toast.success { border-color: rgba(34,197,94,0.3); color: #22c55e; }
.pf-toast.error   { border-color: rgba(239,68,68,0.3); color: #ef4444; }
@keyframes toastIn { from { opacity:0; transform:translateX(-50%) translateY(10px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }

/* Admin banner */
.pf-admin-banner {
  display: flex; align-items: center; gap: 12px;
  background: rgba(234,179,8,0.07);
  border: 1px solid rgba(234,179,8,0.18);
  border-radius: 10px;
  padding: 13px 16px;
  margin-bottom: 20px;
  text-decoration: none;
}
.pf-admin-banner-text { flex: 1; }
.pf-admin-banner-title { font-size: 13px; font-weight: 700; color: #eab308; }
.pf-admin-banner-sub   { font-size: 11px; color: #a07c10; margin-top: 2px; }

/* Member since chip */
.pf-member-chip {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px; color: #5a607a;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 20px; padding: 4px 10px;
  margin-top: 6px;
}

@media (max-width: 480px) {
  .pf-name { font-size: 20px; }
  .pf-avatar { width: 72px; height: 72px; font-size: 26px; }
  .pf-stat-num { font-size: 22px; }
}
`;

function initials(name = "") {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "U";
}

function formatDate(ts) {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function Profile() {
  const { user, userProfile } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  const showToast = (msg, type = "success") => {
    clearTimeout(toastTimer.current);
    setToast({ msg, type });
    toastTimer.current = setTimeout(() => setToast(null), 3000);
  };

  const isAdmin = userProfile?.role === "admin";

  async function handleLogout() {
    setLoading(true);
    try {
      await logout();
      navigate("/login");
    } catch {
      showToast("Failed to sign out", "error");
      setLoading(false);
    }
  }

  async function handleResetPassword() {
    if (!user?.email) return;
    setLoading(true);
    try {
      await resetPassword(user.email);
      showToast("Reset email sent — check your inbox ✓", "success");
    } catch (e) {
      showToast(e.message || "Failed to send reset email", "error");
    }
    setLoading(false);
  }

  if (!user) {
    return (
      <>
        <style>{S}</style>
        <div className="pf-pg">
          <Navbar />
          <div style={{ textAlign: "center", padding: "120px 24px" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#fff", marginBottom: 8 }}>Sign in to view your profile</div>
            <div style={{ fontSize: 14, color: "#5a607a", marginBottom: 24 }}>You need an account to access this page.</div>
            <Link to="/login" style={{ display: "inline-block", background: "#0078F2", color: "#fff", borderRadius: 8, padding: "12px 28px", fontWeight: 700, fontSize: 14, textDecoration: "none" }}>
              Sign In
            </Link>
          </div>
        </div>
      </>
    );
  }

  const displayName = userProfile?.displayName || user.displayName || "Player";
  const email       = userProfile?.email || user.email || "";
  const totalOrders = userProfile?.totalOrders ?? 0;
  const memberSince = formatDate(userProfile?.createdAt);

  return (
    <>
      <style>{S}</style>
      <div className="pf-pg">
        <Navbar />

        {/* Hero */}
        <div className="pf-hero">
          <div className="pf-avatar-row">
            <div className="pf-avatar">
              {initials(displayName)}
              <div className="pf-avatar-online" />
            </div>
            <div className="pf-avatar-info">
              <div className="pf-name">{displayName}</div>
              <div className="pf-email">{email}</div>
              <span className={`pf-role-badge ${isAdmin ? "admin" : "customer"}`}>
                {isAdmin ? "⭐ Admin" : "🎮 Player"}
              </span>
              <div className="pf-member-chip">📅 Member since {memberSince}</div>
            </div>
          </div>
        </div>

        <div className="pf-body">

          {/* Admin shortcut */}
          {isAdmin && (
            <Link to="/admin" className="pf-admin-banner">
              <div style={{ fontSize: 24 }}>⚙️</div>
              <div className="pf-admin-banner-text">
                <div className="pf-admin-banner-title">Admin Dashboard</div>
                <div className="pf-admin-banner-sub">Manage games, orders, customers & more</div>
              </div>
              <div style={{ color: "#a07c10", fontSize: 18 }}>›</div>
            </Link>
          )}

          {/* Stats */}
          <div className="pf-stats">
            <div className="pf-stat">
              <div className="pf-stat-num">{totalOrders}</div>
              <div className="pf-stat-lbl">Orders</div>
            </div>
            <div className="pf-stat">
              <div className="pf-stat-num" style={{ color: "#22c55e" }}>0</div>
              <div className="pf-stat-lbl">Active</div>
            </div>
            <div className="pf-stat">
              <div className="pf-stat-num" style={{ color: "#eab308" }}>0</div>
              <div className="pf-stat-lbl">Wishlist</div>
            </div>
          </div>

          {/* Account info */}
          <div className="pf-section-label">Account</div>
          <div className="pf-card" style={{ marginBottom: 20 }}>
            <div className="pf-info-row">
              <span className="pf-info-label">Display Name</span>
              <span className="pf-info-value">{displayName}</span>
            </div>
            <div className="pf-info-row">
              <span className="pf-info-label">Email</span>
              <span className="pf-info-value">{email}</span>
            </div>
            <div className="pf-info-row">
              <span className="pf-info-label">User ID</span>
              <span className="pf-info-value" style={{ fontSize: 11, color: "#3a3f58", fontFamily: "monospace" }}>
                {user.uid.slice(0, 16)}…
              </span>
            </div>
            <div className="pf-info-row">
              <span className="pf-info-label">Role</span>
              <span className="pf-info-value" style={{ color: isAdmin ? "#eab308" : "#0078F2" }}>
                {isAdmin ? "Administrator" : "Customer"}
              </span>
            </div>
          </div>

          {/* Quick nav */}
          <div className="pf-section-label">Quick Access</div>
          <div className="pf-card" style={{ marginBottom: 20 }}>
            <Link to="/orders" className="pf-nav-link">
              <div className="pf-nav-link-ico" style={{ background: "rgba(0,120,242,0.12)" }}>📦</div>
              <div className="pf-nav-link-body">
                <div>My Orders</div>
                <div className="pf-nav-link-sub">View all your purchases</div>
              </div>
              <span className="pf-nav-arrow">›</span>
            </Link>
            <Link to="/store" className="pf-nav-link">
              <div className="pf-nav-link-ico" style={{ background: "rgba(34,197,94,0.1)" }}>🎮</div>
              <div className="pf-nav-link-body">
                <div>Browse Store</div>
                <div className="pf-nav-link-sub">Discover new games</div>
              </div>
              <span className="pf-nav-arrow">›</span>
            </Link>
            <Link to="/how-to-use" className="pf-nav-link">
              <div className="pf-nav-link-ico" style={{ background: "rgba(167,139,250,0.1)" }}>📖</div>
              <div className="pf-nav-link-body">
                <div>How To Use</div>
                <div className="pf-nav-link-sub">Guide for redeeming games</div>
              </div>
              <span className="pf-nav-arrow">›</span>
            </Link>
            <Link to="/terms" className="pf-nav-link">
              <div className="pf-nav-link-ico" style={{ background: "rgba(255,255,255,0.04)" }}>📄</div>
              <div className="pf-nav-link-body">
                <div>Terms & Privacy</div>
                <div className="pf-nav-link-sub">Legal information</div>
              </div>
              <span className="pf-nav-arrow">›</span>
            </Link>
          </div>

          {/* Actions */}
          <div className="pf-section-label">Account Actions</div>
          <button className="pf-btn pf-btn-reset" onClick={handleResetPassword} disabled={loading}>
            {loading ? "Sending…" : "📧 Send Password Reset Email"}
          </button>
          <button className="pf-btn pf-btn-logout" onClick={handleLogout} disabled={loading}>
            {loading ? "Signing out…" : "Sign Out"}
          </button>

          {/* App version */}
          <div style={{ textAlign: "center", marginTop: 28, color: "#2a2f45", fontSize: 11 }}>
            PhsarGame v1.0.0 · Built with ❤️ in Cambodia
          </div>
        </div>
      </div>

      {toast && (
        <div className={`pf-toast ${toast.type}`}>{toast.msg}</div>
      )}
    </>
  );
}